/**
 * Collection filtering & sorting via the Section Rendering API.
 * - Any change in a [data-facets-form] or the sort select re-fetches the
 *   section with the new params and swaps the results + facet forms in place.
 * - Pagination links inside the results are intercepted the same way.
 * - URL is kept in sync (pushState) and back/forward works (popstate).
 */
window.WsFacets = class WsFacets extends HTMLElement {
  #abort = null;
  #controller = null;
  #debounce = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.addEventListener('input', (event) => this.#onInput(event), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('submit', (event) => {
      if (event.target.matches('[data-facets-form]')) {
        event.preventDefault();
        this.#apply(event.target);
      }
    }, { signal });

    window.addEventListener('popstate', () => this.#render(window.location.search, false), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#controller?.abort();
    clearTimeout(this.#debounce);
  }

  #onChange(event) {
    if (event.target.matches('[data-facets-sort]')) {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', event.target.value);
      params.delete('page');
      this.#render(`?${params}`, true);
      return;
    }
    if (event.target.matches('.ws-facets__checkbox')) {
      this.#apply(event.target.closest('[data-facets-form]'));
    }
  }

  #onInput(event) {
    if (event.target.matches('.ws-facets__price input')) {
      clearTimeout(this.#debounce);
      const form = event.target.closest('[data-facets-form]');
      this.#debounce = setTimeout(() => this.#apply(form), 600);
    }
  }

  #onClick(event) {
    const hideToggle = event.target.closest('[data-filters-hide]');
    if (hideToggle) {
      const hidden = this.toggleAttribute('data-filters-hidden');
      hideToggle.setAttribute('aria-expanded', String(!hidden));
      const label = hideToggle.querySelector('[data-filters-hide-label]');
      if (label) label.textContent = hidden ? hideToggle.dataset.textShow : hideToggle.dataset.textHide;
      return;
    }

    const facetLink = event.target.closest('[data-facets-link], [data-facets-clear]');
    if (facetLink) {
      event.preventDefault();
      const url = new URL(facetLink.href);
      this.#render(url.search, true);
      return;
    }

    const openButton = event.target.closest('[data-filters-open]');
    if (openButton) {
      this.querySelector('[data-filters-drawer]')?.showModal();
      return;
    }
    if (event.target.closest('[data-filters-close]')) {
      this.querySelector('[data-filters-drawer]')?.close();
      return;
    }

    const pageLink = event.target.closest('.ws-collection__pagination a');
    if (pageLink) {
      event.preventDefault();
      const url = new URL(pageLink.href);
      this.#render(url.search, true, true);
    }
  }

  #apply(form) {
    if (!form) return;
    const params = new URLSearchParams(new FormData(form));
    // Preserve current sort when the form carries none (or override with select value)
    const sort = this.querySelector('[data-facets-sort]')?.value;
    if (sort) params.set('sort_by', sort);
    params.delete('page');
    for (const [key, value] of [...params.entries()]) {
      if (value === '') params.delete(key);
    }
    this.#render(`?${params}`, true);
  }

  async #render(search, push, scrollToTop = false) {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const results = this.querySelector('[data-collection-results]');
    results?.setAttribute('aria-busy', 'true');

    const url = `${window.location.pathname}${search}${search.includes('?') ? '&' : '?'}section_id=${this.dataset.sectionId}`;

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const next = new DOMParser().parseFromString(html, 'text/html');

      const nextResults = next.querySelector('[data-collection-results]');
      if (nextResults && results) results.innerHTML = nextResults.innerHTML;

      // Collection: the count lives inside the swapped results (re-created, so a
      // screen reader would not announce it) — mirror it into a persistent live region.
      const status = this.querySelector('[data-facet-status]');
      const countText = nextResults?.querySelector('[role="status"]')?.textContent.trim();
      if (status && countText) status.textContent = countText;

      // Search: its count summary sits outside the swapped results, so it would go
      // stale — update it in place (it is role="status", so the change is announced too).
      const nextSummary = next.querySelector('.ws-search__summary');
      const summary = this.querySelector('.ws-search__summary');
      if (nextSummary && summary) summary.textContent = nextSummary.textContent;

      const currentForms = this.querySelectorAll('.ws-collection__sidebar .ws-facets, .ws-collection__drawer-body .ws-facets');
      const nextForm = next.querySelector('.ws-facets');
      if (nextForm) {
        // Preserve which filter groups the user collapsed across the swap
        const openState = new Map();
        for (const group of this.querySelectorAll('.ws-facets__group')) {
          const label = group.querySelector('.ws-facets__summary span')?.textContent.trim();
          if (label) openState.set(label, group.open);
        }
        for (const form of currentForms) {
          form.innerHTML = nextForm.innerHTML;
          for (const group of form.querySelectorAll('.ws-facets__group')) {
            const label = group.querySelector('.ws-facets__summary span')?.textContent.trim();
            if (label && openState.has(label)) group.open = openState.get(label);
          }
        }
      }

      if (push) history.pushState({}, '', `${window.location.pathname}${search}`);
      if (scrollToTop) this.querySelector('.ws-collection__toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Facets render failed:', error);
        window.location.search = search;
      }
    } finally {
      results?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('ws-facets')) {
  customElements.define('ws-facets', WsFacets);
}
